import React from 'react';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { TripPreferences } from '../../../../data/models/trip-preferences.model';
import preferenceUtils from '../../../../services/preferenceUtils';
import * as referenceSlice from '../../../../data/reference.slice';
import * as providerSlice from '../../../../data/provider.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import TripPreferencesForm from './TripPreferencesForm';
import ElementDialog from '../../ElementDialog';
import TripPreferencesFormInstructions from '../../../onboard/TripPreferencesFormInstructions';

interface TripPreferencesDialogProps {
  title: string;
  open: boolean;
  pref: TripPreferences | null;
  close: (saved: boolean) => void;
  providerId?: number | null;
}

export default function TripPreferencesDialog({
  title,
  open,
  providerId,
  pref,
  close,
}: TripPreferencesDialogProps) {
  const dispatch = useAppDispatch();
  const tripModes = useAppSelector(referenceSlice.selectTripModes);
  const timeZones = useAppSelector(referenceSlice.selectTimeZones);
  const coverageAreas = useAppSelector(providerSlice.selectCoverageAreas);
  const frequencyDayOfWeeks = useAppSelector(
    referenceSlice.selectFrequencyDayOfWeeks,
  );

  const saving = useAppSelector(providerSlice.selectSavingTripPreferences);
  const saved = useAppSelector(providerSlice.selectSavedTripPreferences);
  const savingError = useAppSelector(
    providerSlice.selectSavingTripPreferencesError,
  );

  const { control, formState, handleSubmit, reset, watch, setValue } =
    useForm<TripPreferences>({
      defaultValues: { coverageAreas: [] },
    });

  React.useEffect(() => {
    if (pref && frequencyDayOfWeeks && tripModes) {
      reset(preferenceUtils.getDefaults(pref, frequencyDayOfWeeks, tripModes));
    }
  }, [pref, frequencyDayOfWeeks, tripModes]);

  const sameHoursAllDays = watch('sameHoursAllDays');
  const hours = preferenceUtils.generateTimeOptions();

  const savePreferences = React.useCallback(
    (data: TripPreferences) => {
      if (providerId) {
        const p = preferenceUtils.getPreferencesFromFormData(
          pref,
          frequencyDayOfWeeks,
          data,
        );

        const fn = providerSlice.updateTripPreferencesAsync(providerId, p);
        dispatch(fn);
      }
    },
    [dispatch, pref],
  );

  return (
    <ElementDialog
      title={title}
      faqContext="trip_bundling_preferences_form"
      open={open}
      saving={saving}
      saved={saved}
      savingError={savingError}
      save={handleSubmit(savePreferences)}
      reset={reset}
      close={close}
      formState={formState}>
      <TripPreferencesForm
        watch={watch}
        control={control}
        formState={formState}
        setValue={setValue}
        hours={hours}
        timeZones={timeZones}
        coverageAreas={coverageAreas}
        sameHoursAllDays={sameHoursAllDays}
      />
      <TripPreferencesFormInstructions />
    </ElementDialog>
  );
}
